import React, { useState } from 'react'
import { Box, List, ListItem, ListItemButton, ListItemIcon, Popover, Stack } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import theme from '../../../../theme'
import { type DisplaySettings } from '../../../../hooks/useDisplaySettings'
import DisplayCustomizationDrawer from '../DisplayCustomizationDrawer'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import { ReportControlButtons } from './styles'
import { type ReportsV2, type Report } from '../../../../models/Report'
import CustomTooltip from '../../../../components/CustomTooltip'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { useReportsContext } from '../../ReportsContext'

export interface ReportControlsProps {
  downloadCSVFile: () => Promise<any>
  downloadPDFReport: () => Promise<any>
  requestForCourseSectionFilesInfo?: Record<string, any>
  disableButtons?: boolean
  reports: ReportsV2 | null
  fetchReports: any
  setIsFullScreen: React.Dispatch<React.SetStateAction<boolean>>
  isFullScreen: boolean
}

const ReportsControls: React.FC<ReportControlsProps> = ({
  disableButtons,
  downloadCSVFile,
  downloadPDFReport,
  requestForCourseSectionFilesInfo,
  fetchReports,
  setIsFullScreen,
  isFullScreen
}): JSX.Element => {
  // Single state for managing the drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const { selectedTrend, dispatch } = useReportsContext()

  const requestForFilesEmailSubject = encodeURIComponent('Request for Course Section files')
  const requestForFilesEmailBody = encodeURIComponent(
    `Please complete the missing fields below (e.g., School Email, First Name, Last Name, File Format) in the email before sending.\n\nSchool Email: [Provide your School Email Here]\nFirst Name: [Provide your First Name Here]\nLast Name: [Prove your Last Name Here]\nFile Format: [Choose between PDF/CSV]\n\n---FOR DATA RETRIEVAL: DO NOT MODIFY THE SECTION BELOW---\nTerm: ${requestForCourseSectionFilesInfo?.termTitle ?? ''
    }\nSurvey Project: ${requestForCourseSectionFilesInfo?.surveyProjectTitle ?? ''}\nAcademic Unit: ${requestForCourseSectionFilesInfo?.divisionTitle ?? ''
    }\n`
  )
  const mailtoLink = `mailto:support@classranked.com?subject=${requestForFilesEmailSubject}&body=${requestForFilesEmailBody}`

  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState<null | HTMLElement>(null)
  const isSettingsMenuOpen = Boolean(settingsAnchorEl)
  const openSettingsMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setSettingsAnchorEl(event.currentTarget)
  }
  const closeSettingsMenu = (): void => {
    setSettingsAnchorEl(null)
  }

  const [exportAnchorEl, setExportAnchorEl] = React.useState<null | HTMLElement>(null)
  const isExportMenuOpen = Boolean(exportAnchorEl)
  const openExportMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setExportAnchorEl(event.currentTarget)
  }
  const closeExportMenu = (): void => {
    setExportAnchorEl(null)
  }

  // Disable key stats drawer if no reports are available
  const disabledKeyStatsDrawer = false

  const getDisplaySettingsTooltip = (): string => {
    if (disabledKeyStatsDrawer) {
      return 'Display settings are disabled because no reports are available.'
    }
    if (selectedTrend > 1) {
      return 'Display settings are disabled for multiple historical Terms selections.'
    }
    return ''
  }
  const displaySettingsTooltip = getDisplaySettingsTooltip()

  return (
    <>
    {
      isDrawerOpen &&
      <DisplayCustomizationDrawer
        isModalOpen={isDrawerOpen}
        onClose={() => { setIsDrawerOpen(false) }}
        fetchReports={fetchReports}
      />
    }
      <ReportControlButtons>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          sx={{
            width: '100%',
            flexWrap: 'nowrap'
          }}
        >
          <GenericButton
            fullWidth
            aria-describedby="export-menu"
            label="Export"
            variant="outlined"
            fontWeight="bold"
            endIcon={isExportMenuOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            buttonType={ButtonType.Secondary}
            onClick={openExportMenu}
            aria-controls={isExportMenuOpen ? 'long-menu' : undefined}
            aria-expanded={isExportMenuOpen ? 'true' : undefined}
            aria-haspopup="true"
            disabled={disableButtons}
            style={{
              width: '100%',
              flexShrink: 0,
              ...(theme.breakpoints.up('sm') && { width: 'auto' })
            }}
          />
          <GenericButton
            fullWidth
            variant="outlined"
            buttonType={ButtonType.Secondary}
            aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
            title={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
            icon={isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            onClick={() => { setIsFullScreen(!isFullScreen) }}
          />

          <Box
            sx={{
              width: '100%',
              flexShrink: 0,
              ...(theme.breakpoints.up('sm') && { width: 'auto' })
            }}
          >
            <CustomTooltip title={displaySettingsTooltip}>
              <GenericButton
                fullWidth
                variant="outlined"
                buttonType={ButtonType.Secondary}
                aria-label="Key stats"
                disabled={disabledKeyStatsDrawer || (disableButtons ?? false) || selectedTrend > 1}
                icon={<InsightsOutlinedIcon />}
                onClick={() => { setIsDrawerOpen(true) }}
                style={{
                  width: '100%',
                  flexShrink: 0,
                  ...(theme.breakpoints.up('sm') && { width: '100%' })
                }}
              />
            </CustomTooltip>
          </Box>

          <GenericButton
            fullWidth
            buttonType={ButtonType.Secondary}
            disabled={disableButtons}
            aria-label="Display settings"
            aria-describedby="settings-menu"
            aria-controls={isSettingsMenuOpen ? 'long-menu' : undefined}
            aria-expanded={isSettingsMenuOpen ? 'true' : undefined}
            icon={<MoreVertIcon />}
            onClick={openSettingsMenu}
            style={{
              width: '100%',
              flexShrink: 0,
              ...(theme.breakpoints.up('sm') && { width: 'auto' })
            }}
          />
        </Stack>
      </ReportControlButtons>
      <Popover
        onClose={closeExportMenu}
        open={isExportMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        id="export-menu"
        anchorEl={exportAnchorEl}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                closeExportMenu()
                void downloadPDFReport()
              }}
            >
              Export to PDF
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                closeExportMenu()
                void downloadCSVFile()
              }}
            >
              Export to CSV
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                closeExportMenu()
                window.open(mailtoLink, '_blank')
              }}
            >
              Request Course Section files
              <ListItemIcon
                style={{
                  marginLeft: '4px',
                  minWidth: 'auto',
                  color: theme.palette.text.primary
                }}
              >
                <ArrowOutwardIcon />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
      <Popover
        onClose={closeSettingsMenu}
        open={isSettingsMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        id="settings-menu"
        anchorEl={settingsAnchorEl}
      >
        <List>
          <ListItem disabled disablePadding>
            <ListItemButton>Print</ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </>
  )
}

export default ReportsControls
